import { BoltIcon, BoltSlashIcon, EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline'
import cloneDeep from 'clone-deep'
import { LayoutLoading } from 'components/LayoutLoading'
import { SaveChanges } from 'components/SaveChanges'
import { getInitialLoanFields } from 'config'
import { uwSummaryDefaultInputs } from 'pages/Loan/Underwriting/UnderwritingSummary/constants'
import { useEffect, useState } from 'react'
import { getAdminConfig, setAdminConfig } from 'services'
import { Button } from 'stories/components'
import { RenderInput } from 'utils/RenderInput'

import { inputGroups, IUwSummaryConfig, UwSummarySection, UwSummarySectionText } from './types'

export const UnderwritingSummaryConfig = () => {
  const loanInputs = getInitialLoanFields()
  const uwSummaryInputs = uwSummaryDefaultInputs()

  const [isLoading, setLoading] = useState(true)
  const [isChanged, setChanged] = useState<boolean>()
  const [data, setData] = useState<IUwSummaryConfig>({
    [UwSummarySection.UnderwritingInformation]: {},
    [UwSummarySection.PropertyConstruction]: {},
    [UwSummarySection.BorrowerCreditInfo]: {},
    [UwSummarySection.BorrowerInterestSource]: {},
  })

  useEffect(() => {
    setLoading(true)
    getAdminConfig('uwConfig')
      .then((res) => {
        if (!res) return
        Object.keys(UwSummarySection).map((key) => {
          if (!res[key]) res[key] = {}
        })
        setData(res)
      })
      .finally(() => setLoading(false))
  }, [])

  const onVisible = (section: UwSummarySection, key: string, value: any, props: 'visible' | 'required') => {
    const newData = cloneDeep(data)
    if (!newData[section][key]) newData[section][key] = {}
    newData[section][key][props] = value
    setData(newData)
    setChanged(true)
  }

  const onSubmit = async () => {
    setLoading(true)
    await setAdminConfig('uwConfig', data)
    setLoading(false)
    setChanged(false)
  }

  return (
    <div>
      <LayoutLoading show={isLoading} />

      <div className="grid grid-cols-3 mt-4 gap-2 mb-4">
        {Object.values(UwSummarySection).map((section, index: number) => {
          return (
            <>
              <div className="col-span-full">
                <RenderInput
                  Key={`section${index}`}
                  input={{
                    inputType: 'section',
                    title: `${index + 1}. ${UwSummarySectionText[section]}`,
                  }}
                  onChange={() => {}}
                />
              </div>
              {(inputGroups as any)[section].map((key: string, subIndex: number) => {
                const { visible = true, required = false } = data[section][key] || {}
                const Icon = visible ? EyeIcon : EyeSlashIcon
                const RequiredIcon = required ? BoltIcon : BoltSlashIcon

                return (
                  <div className={`flex justify-between items-center`} key={section + key}>
                    <p className={`text-sm ${visible ? '' : 'line-through opacity-60'}`}>
                      {subIndex + 1}. {uwSummaryInputs[key].title}
                    </p>
                    <div className="flex gap-2 items-center">
                      <span
                        className={`btn-icon ${visible ? '' : 'opacity-60'}`}
                        onClick={() => onVisible(section, key, !visible, 'visible')}
                      >
                        <Icon className="w-4 h-4" />
                      </span>
                      <span
                        className={`btn-icon ${required ? '' : 'opacity-60'}`}
                        onClick={() => onVisible(section, key, !required, 'required')}
                      >
                        <RequiredIcon className="w-4 h-4" />
                      </span>
                    </div>
                  </div>
                )
              })}
              {section === UwSummarySection.UnderwritingInformation &&
                Object.keys(loanInputs).map((key, index) => {
                  const { visible = true, required = false } = data[section][key] || {}
                  const Icon = visible ? EyeIcon : EyeSlashIcon
                  const RequiredIcon = required ? BoltIcon : BoltSlashIcon

                  return (
                    <div className={`flex justify-between items-center`}>
                      <p className={`text-sm ${visible ? '' : 'line-through opacity-60'}`}>
                        {index + 1 + (inputGroups as any)[section].length}. {loanInputs[key].title}
                      </p>
                      <div className="flex gap-2 items-center">
                        <span
                          className={`btn-icon ${visible ? '' : 'opacity-60'}`}
                          onClick={() => onVisible(section, key, !visible, 'visible')}
                        >
                          <Icon className="w-4 h-4" />
                        </span>
                        <span
                          className={`btn-icon ${required ? '' : 'opacity-60'}`}
                          onClick={() => onVisible(section, key, !required, 'required')}
                        >
                          <RequiredIcon className="w-4 h-4" />
                        </span>
                      </div>
                    </div>
                  )
                })}
            </>
          )
        })}
      </div>

      <div className="flex flex-row justify-center gap-4">
        <Button onClick={onSubmit} loading={isLoading} disabled={!isChanged}>
          Save
        </Button>
      </div>

      <SaveChanges show={isChanged} label="Save Changes" onSave={onSubmit} />
    </div>
  )
}
